import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';

import { useIntegrationProviders } from '@/api/integration';
import IntegrationsListActions from '@/app/integrations/IntegrationsListActions';
import { Empty } from '@/components/Empty';
import { Page } from '@/components/page';

import IntegrationCard from './IntegrationCard';
import IntegrationMenu from './IntegrationMenu';
import IntegrationsListContent from './IntegrationsListContent';
import { getProviderLogoById, getProviderShortDescriptionById } from './providerUtil';
import type { ProviderCategoryCounts, ProviderCategoryLists, ProviderCategoryType } from './types';

const IntegrationsList = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState('');
  const providerCategory = searchParams.get('category') as ProviderCategoryType | undefined;
  const { providers } = useIntegrationProviders();

  useEffect(() => {
    if (!providerCategory) {
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set('category', 'all');
      setSearchParams(newSearchParams, { replace: true });
    }
  }, [providerCategory, searchParams, setSearchParams]);

  const searchFilteredProviders = useMemo(() => {
    return providers?.filter((provider) => {
      const name = provider.name.toLowerCase();
      const description = getProviderShortDescriptionById(provider.id)?.toLowerCase();
      return (
        name.includes(search.toLowerCase()) ||
        description?.includes(search.toLowerCase()) ||
        provider.category?.includes(search.toLowerCase())
      );
    });
  }, [providers, search]);

  const providerCategoryLists = useMemo<ProviderCategoryLists>(() => {
    return {
      all: searchFilteredProviders,
      connected: searchFilteredProviders?.filter((provider) =>
        provider.connections.some((connection) => connection.status === 'active'),
      ),
      notConnected: searchFilteredProviders?.filter((provider) => !provider.connections.length),
      error: searchFilteredProviders?.filter((provider) =>
        provider.connections.some((connection) => connection.status === 'inactive'),
      ),
      crm: searchFilteredProviders?.filter((provider) => provider.category === 'crm'),
      advertising: searchFilteredProviders?.filter(
        (provider) => provider.category === 'advertising',
      ),
      ai: searchFilteredProviders?.filter((provider) => provider.category === 'ai'),
    };
  }, [searchFilteredProviders]);

  const providerCounts = useMemo<ProviderCategoryCounts>(() => {
    return {
      all: providerCategoryLists.all?.length,
      connected: providerCategoryLists.connected?.length,
      notConnected: providerCategoryLists.notConnected?.length,
      error: providerCategoryLists.error?.length,
      crm: providerCategoryLists.crm?.length,
      advertising: providerCategoryLists.advertising?.length,
      ai: providerCategoryLists.ai?.length,
    };
  }, [providerCategoryLists]);

  if (!providerCategory) {
    return null;
  }

  const providersList = providerCategoryLists[providerCategory];
  const providersEmpty = !providersList?.length;

  return (
    <Page
      className="overflow-hidden"
      title={t`Settings`}
      pageName={t`Integrations`}
      exampleAIQuestions={[]}
      noPadding
    >
      <IntegrationsListContent>
        <div className="flex flex-col gap-2 overflow-y-auto">
          <div className="flex justify-end gap-2 px-8 py-6">
            <IntegrationsListActions search={search} onSearchChange={setSearch} />
          </div>
          <div className="flex gap-8 max-xl:flex-col max-xl:overflow-y-auto">
            <div className="w-[290px] overflow-y-auto px-8 pr-2 pb-6 max-xl:overflow-visible max-xl:pb-0">
              <IntegrationMenu
                providerCategory={providerCategory}
                providerCategoryCounts={providerCounts}
                onProviderCategoryChange={(category) => {
                  const newSearchParams = new URLSearchParams(searchParams);
                  newSearchParams.set('category', category);
                  setSearchParams(newSearchParams);
                }}
              />
            </div>
            {providersEmpty ? (
              <div className="flex h-full flex-1 justify-center">
                <Empty className="items-start">
                  <Trans>No integrations found.</Trans>
                </Empty>
              </div>
            ) : (
              <div className="grid flex-1 grid-cols-1 gap-4 overflow-y-auto pr-8 pb-6 max-xl:overflow-visible max-xl:px-8 max-xl:pt-0 max-xl:pb-0 min-[2000px]:grid-cols-5 min-[2400px]:grid-cols-6 md:grid-cols-2 xl:grid-cols-3 xl:gap-6 2xl:grid-cols-4 2xl:gap-8">
                {providersList?.map((provider) => (
                  <IntegrationCard
                    key={provider.id}
                    id={provider.id}
                    name={provider.name}
                    description={getProviderShortDescriptionById(provider.id)}
                    category={provider.category}
                    available={provider.available}
                    status={
                      provider.connections.length
                        ? provider.connections[0].status === 'active'
                          ? 'active'
                          : 'broken'
                        : 'notConnected'
                    }
                    icon={getProviderLogoById(provider.id)}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </IntegrationsListContent>
    </Page>
  );
};

export default IntegrationsList;
